import React, { startTransition } from 'react'
import { VideoCall, MissedVideoCall } from '@mui/icons-material'
import { IUploadedVideo, useVideoRecorder } from 'hooks/video'
import { graphql } from 'relay-runtime'
import { useFragment, useLazyLoadQuery } from 'react-relay'
import VideoRecorder from 'components/VideoRecorder'
import Button from 'components/Button'
import Box from 'components/Box'
import { VideoField_ProfileVideoQuery } from '__generated__/VideoField_ProfileVideoQuery.graphql'
import { Typography } from '@mui/material'
import { useFetchKey } from 'hooks/relay'
import { VideoField_User$key } from '__generated__/VideoField_User.graphql'
import { arrayFilterNonNullable } from 'services/array'
import { defaultTeleprompterText } from './utils/defaultTeleprompterText'
import useStyles from './styles'
import StoredVideo from './components/StoredVideo'

export interface IVideoFieldProps {
  user: VideoField_User$key
  editable: boolean
  onUploadStart?: () => void
  onOpen?: () => void
  onClose?: () => void
}

export const PROFILE_VIDEO_FIELD_ID = 'profile-video-area'

function VideoField(props: IVideoFieldProps) {
  const { classes } = useStyles()
  const { editable, onOpen, onClose, user: userFragmentRef } = props
  const [fetchKey, incrementFetchKey] = useFetchKey()

  const user = useFragment(graphql`
    fragment VideoField_User on User {
      video {
        id
      }
    }
  `, userFragmentRef)

  const videoId = user?.video?.id
  const videoData = useLazyLoadQuery<VideoField_ProfileVideoQuery>(
    graphql`
      query VideoField_ProfileVideoQuery($videoId: ID) {
        currentUser {
          status
          configuration {
            videoMaxDuration
            videoMinDuration
          }
        }
        featuredFreelancers {
          video {
            ...SampleVideosButton_SampleVideos
          }
        }
        video: optionalNode(id: $videoId) {
          ... on Video {
            id
            url
            question {
              title
            }

            ...StoredVideo_Video
          }
        }
      }
    `,
    {
      videoId,
    },
    {
      fetchPolicy: 'network-only', // needed for video to show after recording (plus video wouldn't load without network anyways)
      fetchKey,
    },
  )

  const sampleVideos = React.useMemo(() => videoData?.featuredFreelancers?.map(f => f?.video)?.filter(arrayFilterNonNullable) || [], [videoData])
  const video = videoData?.video

  const handleUploadCompleted = React.useCallback((newVideo: IUploadedVideo) => {
    // Trigger a reload of the video
    if (newVideo?.id) {
      startTransition(() => {
        incrementFetchKey()
      })
    }
  }, [incrementFetchKey])

  const { isUploading, videoRecorderProps, openVideoRecorder, saveOtherVideo, isVideoRecorderOpen, error, uploadProgress } = useVideoRecorder({
    onUploadCompleted: handleUploadCompleted,
    onOpen: onOpen,
    onClose: onClose,
  })

  // TODO: fix this with proper error handling?
  const meta = {
    error: null as string | null,
    touched: false,
  }
  const showError = editable && meta.error && meta.touched

  if (editable && videoData?.currentUser?.status === 'pending' && !video) {
    return null
  }

  if (!editable && !video) {
    return null
  }

  return (
    <div id={PROFILE_VIDEO_FIELD_ID}>
      <div className={classes.container} style={editable ? undefined : { paddingBottom: 0 }}>
        <StoredVideo
          videoFragmentRef={video}
          isUploading={isUploading}
          editable={editable}
          uploadProgress={uploadProgress}
          onFileDrop={saveOtherVideo}
        />

        {editable && !isUploading && (
          <React.Fragment>
            {isVideoRecorderOpen && (
              <VideoRecorder
                {...videoRecorderProps}
                title={video?.question?.title || 'Your Video Introduction'}
                defaultVideoURL={video?.url}
                defaultTeleprompterText={defaultTeleprompterText}
                sampleVideosFragmentsRefs={video?.question?.title ? undefined : sampleVideos}
                maxDuration={videoData?.currentUser?.configuration?.videoMaxDuration}
                minDuration={videoData?.currentUser?.configuration?.videoMinDuration}
              />
            )}

            <Box disableTopPadding={!video} disableBottomPadding={!video}>
              <Button
                title={video?.question?.title || 'Your Video Introduction'}
                data-cy="open-video-introduction"
                onClick={openVideoRecorder}
                startIcon={video ? <MissedVideoCall /> : <VideoCall />}
              >
                {video ? 'Replace Video Introduction' : 'Record Video Introduction'}
              </Button>
            </Box>

            {!video && (
              <Typography variant="body1" className={classes.dropHelperText}>
                Please record a brief video introduction describing who you are,
                where you're from, your background, education and work experience etc.
                Good videos help hugely to work with top clients.
                You can use our built in recorder and teleprompter and check out sample videos for inspiration or upload an an existing video if you prefer.
              </Typography>
            )}

            {error && (
              <Typography variant="body1" className={classes.error}>
                {error.message}
              </Typography>
            )}
          </React.Fragment>
        )}

        {editable && showError && (
          <div className={classes.error}>
            {meta.error}
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(VideoField)
